import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  Button,
  Dialog,
  Grid,
  Tab,
  Tabs,
  Typography,
  DialogActions,
  DialogContent,
  Tooltip,
  Fab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  CircularProgress,
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  ListItem,
  ListItemText,
  InputAdornment,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AssessmentIcon from '@material-ui/icons/Assessment';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import StorageIcon from '@material-ui/icons/Storage';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import SearchIcon from '@material-ui/icons/Search';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import InboxIcon from '@material-ui/icons/Inbox';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { TabContext, TabPanel } from '@material-ui/lab';
import List from '@material-ui/core/List';
import Chart from 'chart.js/auto';
import debounce from 'lodash.debounce';

import useApi from '../../../utils/useApi';
import UIContainer from '../../UI/Container/UIContainer';
import UIFormTitle from '../../UI/Form/Title/UIFormTitle';
import StoreContext from '../../Store/context';
import { useTutorial } from '../../UI/Tutorial/TutorialProvider';
import { formattedCurrency, formattedDateTime } from '../../../utils/utils';
import UIDialogConfirm from '../../UI/Dialog/Confirm/UIDialogConfirm';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import StockTable from '../Table/stockTable';
import EntriesTable from '../Table/entriesTable';
import OutsTable from '../Table/outsTable';
import StockReplenishmentView from '../Replenishment/StockReplenishmentView';

// --- ESTILOS PARA O RELATÓRIO ABC ---
const useAbcStyles = makeStyles((theme) => ({
  dashboardCard: {
    padding: theme.spacing(1, 2),
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: '100%',
  },
  cardIcon: {
    padding: '8px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitle: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  cardValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1e293b',
  },
  filterButton: {
    borderRadius: '20px',
    textTransform: 'none',
  },
  badgeA: { backgroundColor: '#dcfce7', color: '#166534', fontWeight: 'bold' },
  badgeB: { backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: 'bold' },
  badgeC: { backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: 'bold' },

  statusUrgent: { backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' },
  statusOk: { backgroundColor: '#f0fdf4', color: '#22c55e', border: '1px solid #bbf7d0' },
  statusWarning: { backgroundColor: '#fffbeb', color: '#f59e0b', border: '1px solid #fde68a' },
  statusInactive: { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' },

  marginHigh: { color: '#22c55e', fontWeight: 700 },
  marginMid: { color: '#f59e0b', fontWeight: 600 },
  marginLow: { color: '#ef4444', fontWeight: 700 },

  chartContainer: {
    height: '300px',
    width: '100%',
  },
  accordion: {
    boxShadow: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: '8px !important',
    marginBottom: theme.spacing(2),
    '&:before': { display: 'none' },
  },
  tableContainer: {
    maxHeight: '450px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  // Mobile card styles for ABC table
  mobileCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  mobileCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: 'none',
    overflow: 'hidden',
    '&:hover': { borderColor: '#cbd5e1' },
  },
  mobileCardContentInner: {
    padding: '12px 14px !important',
    '&:last-child': { paddingBottom: '12px !important' },
  },
  mobileCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  mobileProductName: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#1e293b',
    lineHeight: 1.3,
  },
  mobileCardMetrics: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    padding: '10px 0',
    borderTop: '1px solid #f1f5f9',
    borderBottom: '1px solid #f1f5f9',
  },
  mobileMetricItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  mobileMetricLabel: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  mobileCardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
  },
  // Responsive filter container
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    marginBottom: '8px',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  searchField: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '250px',
    },
  },
  filterButtonGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(5, 2),
    textAlign: 'center',
  },
}));

// --- ESTILOS PARA O COMPONENTE PRINCIPAL ---
const useMainStyles = makeStyles((theme) => ({
  vigencyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    transition: 'all 0.2s ease',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#f1f5f9',
      borderColor: '#cbd5e1',
    },
  },
  footerValues: {
    display: 'flex',
    gap: '12px',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '8px',
    },
  },
  footerValueCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    flex: 1,
  },
  periodListItem: {
    borderRadius: '8px !important',
    marginBottom: '4px !important',
    transition: 'background-color 0.15s ease !important',
  },
  fabVoltar: {
    backgroundColor: '#17497F !important',
    color: '#fff !important',
    position: 'fixed !important',
    bottom: '24px !important',
    left: '50% !important',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    '&:hover': {
      backgroundColor: '#0f3461 !important',
    },
  },
  emptyStateMain: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
  },
  loadingOverlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
  newVigencyBtn: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

// --- COMPONENTE INTERNO: RELATÓRIO ABC ---
const AbcAnalysis = ({ stockId, mobile }) => {
  const classes = useAbcStyles();
  const chartRef = useRef(null);
  const canvasRef = useRef(null);

  // API Hook
  const [stockABCDataLoad, stockABCDataInfo] = useApi({
    method: 'get',
    url: '/reports/stock/abc',
  });

  // Carregar dados ao montar ou mudar o ID do estoque
  useEffect(() => {
    stockABCDataLoad({
      params: { stockId }
    });
  }, [stockId]);

  // Dados brutos vindos da API ou array vazio
  const rawData = stockABCDataInfo.data || [];
  const loading = stockABCDataInfo.loading;

  const [filter, setFilter] = useState('URGENTE');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Debounce search to avoid re-rendering chart on every keystroke
  const debouncedSetSearch = useCallback(
    debounce((value) => setSearchTerm(value), 300),
    []
  );

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    debouncedSetSearch(e.target.value);
  };

  // Filtragem e Busca
  const filteredData = useMemo(() => {
    let data = [...rawData]; // Criar cópia para não mutar o original

    if (filter !== 'all') {
      if (filter === 'URGENTE') {
        data = data.filter(item => item.Sugestao && (item.Sugestao.includes('URGENTE') || item.Sugestao.includes('RUPTURA') || item.Sugestao.includes('REPOR')));
      } else if (filter === 'EXCESSO') {
        data = data.filter(item => item.Sugestao && (item.Sugestao.includes('EXCESSO') || item.Sugestao.includes('SOBRE') || item.Sugestao.includes('PARADO')));
      } else {
        data = data.filter(item => item.Curva === filter);
      }
    }

    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      data = data.filter(item =>
        (item.Produto && item.Produto.toLowerCase().includes(lowerTerm)) ||
        (item.Codigo && item.Codigo.toString().includes(lowerTerm))
      );
    }

    // Ordenação padrão por Valor Movimentado
    return data.sort((a, b) => b.Valor_Movimentado - a.Valor_Movimentado);
  }, [rawData, filter, searchTerm]);

  // Totais para o Dashboard
  const totals = useMemo(() => {
    if (!rawData.length) return { revenue: 0, countA: 0, countUrgent: 0, countExcess: 0 };
    return {
      revenue: rawData.reduce((acc, item) => acc + (Number(item.Valor_Movimentado) || 0), 0),
      countA: rawData.filter(i => i.Curva === 'A').length,
      countUrgent: rawData.filter(i => i.Sugestao && (i.Sugestao.includes('URGENTE') || i.Sugestao.includes('RUPTURA') || i.Sugestao.includes('REPOR'))).length,
      countExcess: rawData.filter(i => i.Sugestao && (i.Sugestao.includes('EXCESSO') || i.Sugestao.includes('SOBRE') || i.Sugestao.includes('PARADO'))).length
    };
  }, [rawData]);

  // Renderização do Gráfico
  useEffect(() => {
    if (!canvasRef.current || loading) return;

    const ctx = canvasRef.current.getContext('2d');

    // Destruir gráfico anterior
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const labels = filteredData.map(item => item.Produto.length > 15 ? item.Produto.substring(0, 15) + '...' : item.Produto);
    const values = filteredData.map(item => Number(item.Valor_Movimentado) || 0);

    // Calculo Pareto
    const total = values.reduce((a, b) => a + b, 0);
    let accumulated = 0;
    const percentages = values.map(val => {
      accumulated += val;
      return total === 0 ? 0 : (accumulated / total) * 100;
    });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Faturamento (R$)',
            data: values,
            backgroundColor: 'rgba(59, 130, 246, 0.7)',
            order: 2,
            yAxisID: 'y'
          },
          {
            label: '% Acumulada',
            data: percentages,
            type: 'line',
            borderColor: '#ef4444',
            borderWidth: 2,
            pointRadius: 2,
            tension: 0.1,
            order: 1,
            yAxisID: 'y1'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || '';
                if (label) label += ': ';
                if (context.dataset.yAxisID === 'y') {
                  label += formattedCurrency(context.raw);
                } else {
                  label += context.raw.toFixed(1) + '%';
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: { type: 'linear', position: 'left', grid: { color: '#e2e8f0' } },
          y1: { type: 'linear', position: 'right', min: 0, max: 105, grid: { display: false } },
          x: { grid: { display: false } }
        }
      }
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [filteredData, loading]);

  // Renderização das Badges
  const getCurvaChip = (curva) => {
    let chipClass = '';
    if (curva === 'A') chipClass = classes.badgeA;
    if (curva === 'B') chipClass = classes.badgeB;
    if (curva === 'C') chipClass = classes.badgeC;
    // Using className logic via classes object for MUI v4 style
    return <Chip label={curva} size="small" className={chipClass} />;
  };

  const getSugestaoStyle = (sugestao) => {
    if (!sugestao) return '';
    if (sugestao.includes('INATIVO')) return classes.statusInactive;
    if (sugestao.includes('URGENTE') || sugestao.includes('RUPTURA') || sugestao.includes('REPOR')) return classes.statusUrgent;
    if (sugestao.includes('EXCESSO') || sugestao.includes('SOBRE') || sugestao.includes('PARADO')) return classes.statusWarning;
    return classes.statusOk;
  };

  const getMarginClass = (margem) => {
    if (margem < 10) return classes.marginLow;
    if (margem >= 30) return classes.marginHigh;
    return classes.marginMid;
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Grid container spacing={2} style={{ padding: '8px', width: '100%', margin: 0 }}>
      {/* Aviso de Janela Temporal */}
      <Grid item xs={12}>
        <div style={{ backgroundColor: '#f8fafc', padding: '10px 15px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TrendingUpIcon style={{ color: '#64748b', fontSize: '1.2rem' }} />
          <Typography variant="body2" style={{ color: '#475569', fontWeight: 500 }}>
            Análise baseada na performance de vendas dos <strong>últimos 90 dias</strong> (independente do período de vigência atual).
          </Typography>
        </div>
      </Grid>

      {/* Dashboard Compacto */}
      <Grid item xs={12} sm={6} md={3}>
        <div className={classes.dashboardCard}>
          <div className={classes.cardIcon} style={{ backgroundColor: '#eff6ff' }}><TrendingUpIcon style={{ color: '#3b82f6' }} /></div>
          <div className={classes.cardContent}>
            <Typography className={classes.cardTitle}>Faturamento Total</Typography>
            <Typography className={classes.cardValue}>{formattedCurrency(totals.revenue)}</Typography>
          </div>
        </div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <div className={classes.dashboardCard}>
          <div className={classes.cardIcon} style={{ backgroundColor: '#ecfdf5' }}><AssessmentIcon style={{ color: '#10b981' }} /></div>
          <div className={classes.cardContent}>
            <Typography className={classes.cardTitle}>Itens Curva A</Typography>
            <Typography className={classes.cardValue}>{totals.countA}</Typography>
          </div>
        </div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <div className={classes.dashboardCard} style={{ borderColor: '#fecaca', borderLeft: '4px solid #ef4444' }}>
          <div className={classes.cardIcon} style={{ backgroundColor: '#fef2f2' }}><ErrorOutlineIcon style={{ color: '#ef4444' }} /></div>
          <div className={classes.cardContent}>
            <Typography className={classes.cardTitle}>Risco Ruptura</Typography>
            <Typography id="tour-stock-urgent" className={classes.cardValue} style={{ color: '#ef4444' }}>{totals.countUrgent}</Typography>
          </div>
        </div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <div className={classes.dashboardCard}>
          <div className={classes.cardIcon} style={{ backgroundColor: '#fffbeb' }}><StorageIcon style={{ color: '#f59e0b' }} /></div>
          <div className={classes.cardContent}>
            <Typography className={classes.cardTitle}>Excesso Estoque</Typography>
            <Typography className={classes.cardValue}>{totals.countExcess}</Typography>
          </div>
        </div>
      </Grid>

      {/* Gráfico Colapsável */}
      <Grid item xs={12}>
        <Accordion className={classes.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#64748b' }}>📊 Análise Visual (Gráfico de Pareto)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.chartContainer}>
              <canvas ref={canvasRef} />
            </div>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/* Filtros e Busca — Responsivo */}
      <Grid item xs={12}>
        <div className={classes.filterContainer}>
          <TextField
            id="tour-stock-search"
            placeholder="Buscar produto..."
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={handleSearchChange}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#94a3b8', fontSize: '1.1rem' }} />
                </InputAdornment>
              ),
              style: { padding: '4px 8px' },
            }}
          />
          <div id="tour-stock-filters" className={classes.filterButtonGroup}>
            <Button variant={filter === 'all' ? "contained" : "outlined"} color="primary" size="small" className={classes.filterButton} onClick={() => setFilter('all')} aria-pressed={filter === 'all'}>Todos</Button>
            <Button variant={filter === 'A' ? "contained" : "outlined"} color="primary" size="small" className={classes.filterButton} onClick={() => setFilter('A')} aria-pressed={filter === 'A'}>Curva A</Button>
            <Button variant={filter === 'B' ? "contained" : "outlined"} color="primary" size="small" className={classes.filterButton} onClick={() => setFilter('B')} aria-pressed={filter === 'B'}>Curva B</Button>
            <Button variant={filter === 'C' ? "contained" : "outlined"} color="primary" size="small" className={classes.filterButton} onClick={() => setFilter('C')} aria-pressed={filter === 'C'}>Curva C</Button>
            <Button id="tour-stock-excess-filter" variant={filter === 'EXCESSO' ? "contained" : "outlined"}
              style={{
                borderColor: '#f59e0b',
                color: filter === 'EXCESSO' ? '#fff' : '#f59e0b',
                backgroundColor: filter === 'EXCESSO' ? '#f59e0b' : 'transparent',
              }}
              size="small"
              onClick={() => setFilter('EXCESSO')}
              className={classes.filterButton}
              aria-pressed={filter === 'EXCESSO'}
            >
              📦 Excesso
            </Button>
            <Button id="tour-stock-urgent-filter" variant={filter === 'URGENTE' ? "contained" : "outlined"}
              style={{
                borderColor: '#ef4444',
                color: filter === 'URGENTE' ? '#fff' : '#ef4444',
                backgroundColor: filter === 'URGENTE' ? '#ef4444' : 'transparent',
              }}
              size="small"
              onClick={() => setFilter('URGENTE')}
              className={classes.filterButton}
              aria-pressed={filter === 'URGENTE'}
            >
              ⚠️ Repor
            </Button>
          </div>
        </div>
      </Grid>

      {/* Produtos — Mobile: Cards / Desktop: Tabela */}
      <Grid item xs={12}>
        {filteredData.length === 0 ? (
          <div className={classes.emptyState}>
            <InboxIcon style={{ fontSize: 48, color: '#cbd5e1', marginBottom: 12 }} />
            <Typography variant="body2" style={{ color: '#64748b', fontWeight: 500 }}>
              Nenhum produto encontrado para o filtro selecionado.
            </Typography>
          </div>
        ) : mobile ? (
          /* Mobile Card View */
          <div id="tour-stock-table" className={classes.mobileCardList}>
            {filteredData.map((row) => (
              <Card key={row.Codigo || Math.random()} className={classes.mobileCard}>
                <CardContent className={classes.mobileCardContentInner}>
                  <div className={classes.mobileCardHeader}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                      <Typography className={classes.mobileProductName} noWrap>{row.Produto}</Typography>
                      <Typography variant="caption" style={{ color: '#94a3b8' }}>Cód: {row.Codigo}</Typography>
                    </div>
                    {getCurvaChip(row.Curva)}
                  </div>
                  <div className={classes.mobileCardMetrics}>
                    <div className={classes.mobileMetricItem}>
                      <Typography className={classes.mobileMetricLabel}>Estoque</Typography>
                      <Typography variant="body2" style={{ fontWeight: 600, color: row.Estoque_Fisico <= 0 ? '#ef4444' : '#1e293b' }}>
                        {row.Estoque_Fisico} un
                      </Typography>
                    </div>
                    <div className={classes.mobileMetricItem}>
                      <Typography className={classes.mobileMetricLabel}>Margem</Typography>
                      <Typography variant="body2" className={getMarginClass(Number(row.Margem) || 0)}>
                        {Number(row.Margem || 0).toFixed(1)}%
                      </Typography>
                    </div>
                    <div className={classes.mobileMetricItem}>
                      <Typography className={classes.mobileMetricLabel}>Saída 90d</Typography>
                      <Typography variant="body2" style={{ fontWeight: 500, color: '#1e293b' }}>{row.Saida_90_Dias}</Typography>
                    </div>
                    <div className={classes.mobileMetricItem}>
                      <Typography className={classes.mobileMetricLabel}>Cobertura</Typography>
                      <Typography variant="body2" style={{ fontWeight: 500, color: '#1e293b' }}>
                        {row.Dias_De_Cobertura > 365 ? '999+' : row.Dias_De_Cobertura} d
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.mobileCardFooter}>
                    <Typography variant="body2" style={{ fontWeight: 700, color: '#1e293b' }}>
                      {formattedCurrency(Number(row.Valor_Movimentado) || 0)}
                    </Typography>
                    <Chip label={row.Sugestao} size="small" className={getSugestaoStyle(row.Sugestao)} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Desktop Table View */
          <TableContainer id="tour-stock-table" component={Paper} className={classes.tableContainer}>
            <Table stickyHeader size="small" aria-label="Tabela Curva ABC">
              <TableHead>
                <TableRow>
                  <TableCell style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Cód</TableCell>
                  <TableCell style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Produto</TableCell>
                  <TableCell align="right" style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Estoque</TableCell>
                  <TableCell align="right" style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Margem</TableCell>
                  <TableCell align="right" style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Saída (90d)</TableCell>
                  <TableCell align="right" style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Cobertura</TableCell>
                  <TableCell align="right" style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Valor Mov.</TableCell>
                  <TableCell align="center" style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Classe</TableCell>
                  <TableCell align="center" style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>Sugestão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((row) => (
                  <TableRow key={row.Codigo || Math.random()} hover>
                    <TableCell>{row.Codigo}</TableCell>
                    <TableCell>{row.Produto}</TableCell>
                    <TableCell align="right" style={{ fontWeight: row.Estoque_Fisico <= 0 ? 'bold' : 'normal', color: row.Estoque_Fisico <= 0 ? '#ef4444' : 'inherit' }}>
                      {row.Estoque_Fisico}
                    </TableCell>
                    <TableCell align="right" className={getMarginClass(Number(row.Margem) || 0)}>
                      {Number(row.Margem || 0).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">{row.Saida_90_Dias}</TableCell>
                    <TableCell align="right">{row.Dias_De_Cobertura > 365 ? '999+' : row.Dias_De_Cobertura} d</TableCell>
                    <TableCell align="right">{formattedCurrency(Number(row.Valor_Movimentado) || 0)}</TableCell>
                    <TableCell align="center">{getCurvaChip(row.Curva)}</TableCell>
                    <TableCell align="center">
                      <Chip label={row.Sugestao} size="small" className={getSugestaoStyle(row.Sugestao)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  );
};


// --- COMPONENTE PRINCIPAL ---
const CashierClosuresView = () => {
  const { credencials } = useContext(StoreContext);
  const mainClasses = useMainStyles();

  // verificar se é mobile
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  // define os valores de outros no grafico
  const [othersValues, setOthersValues] = useState(null);

  const [selectNewClosure, setNewClosure] = useState(false);

  const [closeStockModal, setOpenedClosedModal] = useState(false);

  // modais de pesquisa e filtragem
  const [tab, setTab] = useState('5');

  const [index, setIndex] = useState(0);
  const { currentTour, setOverrideTourKey } = useTutorial();

  useEffect(() => {
    const tourKeyMap = {
      '1': 'stockClosures',
      '2': 'stockEntries',
      '3': 'stockOuts',
      '4': 'stockSuppliers',
      '5': 'stockAbc',
    };
    if (setOverrideTourKey) {
      setOverrideTourKey(tourKeyMap[tab] || 'stockAbc');
    }
    return () => {
      if (setOverrideTourKey) setOverrideTourKey(null);
    };
  }, [tab, setOverrideTourKey]);

  useEffect(() => {
    if (currentTour === 'stockSuppliers' && tab !== '4') {
      setTab('4');
    } else if (currentTour === 'stockAbc' && tab !== '5') {
      setTab('5');
    }
  }, [currentTour, tab]);

  const handleTab = (event, newValue) => {
    setTab(newValue);
    if (othersValues) setOthersValues(null);
  };

  const handleModalCloseStock = () => {
    setOpenedClosedModal(!closeStockModal);
  }

  const handleModalSelectVigency = () => {
    setNewClosure(!selectNewClosure);
  };

  const [stockMovesLoad, cashierMoveInfo] = useApi({
    method: 'get',
    params: { withMoves: 1 },
    url: '/stock',
  });

  const [alert, setAlert] = useState(false);
  const [setCloseStock, closeStockInfo] = useApi({
    method: 'delete',
    url: '/stock',
  });

  function closeStock() {
    setCloseStock();
    setAlert(true);
    handleModalCloseStock();
  }

  function closeAlert(event, reason) {
    stockMovesLoad({
      method: 'get',
      params: { withMoves: 1 },
      onCompleted: (res) => {
        if (res) {
          setIndex(0);
        }
      },
    });
    if (reason === 'clickaway') {
      return;
    }
    setAlert(false);
  }

  useEffect(() => {
    stockMovesLoad({
      method: 'get',
      params: { withMoves: 1 },
      onCompleted: (res) => {
        if (res) {
          setIndex(0);
        }
      },
    });
  }, [credencials]);

  function openOtherValues(data, tabIndex) {
    setOthersValues(data);
    setTab(tabIndex);
  }

  function setNewIndex(_index) {
    setIndex(_index);
    if (selectNewClosure) handleModalSelectVigency();
    setTab('1');
  }

  function toggleBackForOthers() {
    setOthersValues(!othersValues);
    setTab('4');
  }

  return (
    <>
      <UIContainer container>
        <Grid
          item
          container
          justify="space-between"
          alignItems="center"
          xs={12}
        >
          <UIFormTitle title="Meu Estoque" />
        </Grid>
        {cashierMoveInfo.loading ? (
          <div className={mainClasses.loadingOverlay}>
            <CircularProgress size={28} />
          </div>
        ) : cashierMoveInfo.data && cashierMoveInfo.data[index] ? (
          <div
            className={mainClasses.vigencyButton}
            onClick={handleModalSelectVigency}
            role="button"
            tabIndex={0}
          >
            <CalendarTodayIcon style={{ fontSize: '1rem', color: '#64748b' }} />
            <Typography variant="body2" style={{ color: '#475569', fontWeight: 500 }}>
              {!cashierMoveInfo.data[index].dueDate
                ? 'Em aberto desde: '
                : 'Aberto '}
              {formattedDateTime(cashierMoveInfo.data[index].startDate)}
              {cashierMoveInfo.data[index].dueDate ? ' e fechado ' : ' '}
              {formattedDateTime(cashierMoveInfo.data[index].dueDate)}
            </Typography>
            <ArrowDropDownIcon style={{ fontSize: '1.2rem', color: '#94a3b8' }} />
          </div>
        ) : (
          <div className={mainClasses.emptyStateMain}>
            <InboxIcon style={{ fontSize: 56, color: '#cbd5e1', marginBottom: 12 }} />
            <Typography variant="h6" style={{ color: '#64748b', fontWeight: 600 }}>
              Nenhum dado de estoque
            </Typography>
            <Typography variant="body2" style={{ color: '#94a3b8', marginTop: 6 }}>
              Cadastre produtos e faça movimentações para visualizar o estoque.
            </Typography>
          </div>
        )}
      </UIContainer>
      <UIContainer style={{ minHeight: '60%', marginBottom: 16 }}>
        <TabContext value={tab}>
          <Grid item xs>
            <Tabs
              value={tab}
              onChange={handleTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab id="tour-stock-closures-tab" value="1" label="Fechamentos" />
              <Tab id="tour-stock-entries-tab" value="2" label="Entradas" />
              <Tab id="tour-stock-outs-tab" value="3" label="Saídas" />
              <Tab id="tour-stock-replenishment-tab" value="4" label="Fornecedores" />
              <Tab id="tour-stock-abc-tab" value="5" label="Curva ABC" /> {/* NOVA ABA */}
            </Tabs>
          </Grid>

          {cashierMoveInfo.data && cashierMoveInfo.data[index] && (
            <>
              <TabPanel value="1" index="1" style={{ padding: 0 }}>
                <StockTable
                  actualId={cashierMoveInfo.data[index].id}
                  doubleClickAction={setNewIndex}
                  mobile={mobile}
                  data={cashierMoveInfo.data}
                  loading={cashierMoveInfo.loading}
                  error={cashierMoveInfo.error}
                />
              </TabPanel>
              <TabPanel
                value="2"
                index="2"
                style={{ padding: 0 }}
              >
                <Grid container style={{ width: '100%', height: '100%' }}>
                  <EntriesTable
                    mobile={mobile}
                    data={cashierMoveInfo.data[index].entries.moves}
                    totalValue={cashierMoveInfo.data[index].entries.total}
                    loading={cashierMoveInfo.loading}
                    error={cashierMoveInfo.error}
                  />
                </Grid>
              </TabPanel>
              <TabPanel
                value="3"
                index="3"
                style={{ padding: 0 }}
              >
                <Grid container style={{ width: '100%', height: '100%' }}>
                  <OutsTable
                    mobile={mobile}
                    data={cashierMoveInfo.data[index].outs.moves}
                    totalValue={cashierMoveInfo.data[index].outs.total}
                    loading={cashierMoveInfo.loading}
                    error={cashierMoveInfo.error}
                  />
                </Grid>
              </TabPanel>
              <TabPanel
                value="4"
                index="4"
                style={{ padding: 0 }}
              >
                <StockReplenishmentView
                  stockId={cashierMoveInfo.data[index].id}
                  mobile={mobile}
                />
              </TabPanel>

              <TabPanel value="5" index="5" style={{ padding: 0 }}>
                <AbcAnalysis stockId={cashierMoveInfo.data[index].id} mobile={mobile} />
              </TabPanel>

            </>
          )}
        </TabContext>
      </UIContainer>
      <UIContainer
        container
        direction="row"
        alignItems="center"
        style={{ minHeight: mobile ? 'auto' : '80px', marginBottom: 24 }}
      >
        <Grid item xs={12} md={10}>
          <div className={mainClasses.footerValues}>
            <div className={mainClasses.footerValueCard} style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Typography variant="caption" style={{ color: '#64748b', fontWeight: 500 }}>Valor do estoque:</Typography>
              <Typography variant="body1" style={{
                fontWeight: 700,
                margin: 0,
                color: cashierMoveInfo.data && cashierMoveInfo.data[index] && cashierMoveInfo.data[index].total < 0 ? '#ef4444' : '#16a34a',
              }}>
                {cashierMoveInfo.data && cashierMoveInfo.data[index] && formattedCurrency(cashierMoveInfo.data[index].value)}
              </Typography>
            </div>
            <div className={mainClasses.footerValueCard} style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" style={{ color: '#64748b', fontWeight: 500 }}>CMV:</Typography>
              <Typography variant="body1" style={{ fontWeight: 700, margin: 0, color: '#1e293b' }}>
                {cashierMoveInfo.data && cashierMoveInfo.data[index] && formattedCurrency(cashierMoveInfo.data[index].cmv)}
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid xs={12} md={2} item container style={{ marginTop: mobile ? 8 : 0 }}>
          <Grid item xs />
          <Button
            variant="outlined"
            size="large"
            color="secondary"
            onClick={handleModalCloseStock}
            className={mainClasses.newVigencyBtn}
          >
            NOVA VIGÊNCIA
          </Button>
        </Grid>
      </UIContainer>

      {/* Dialog: Confirmar Fechamento */}
      <Dialog
        open={closeStockModal}
        fullWidth
        onClose={handleModalCloseStock}
        aria-labelledby="close-stock-dialog-title"
      >
        <UIDialogTitle id="close-stock-dialog-title">Encerrar período de estoque atual?</UIDialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: '#475569', lineHeight: 1.6 }}>
            Esta ação marcará o período atual como data de fechamento de estoque e iniciará uma <strong>nova vigência com CMV zerado</strong>.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={handleModalCloseStock} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={closeStock}
            variant="contained"
            style={{ backgroundColor: '#D60429', color: '#fff' }}
          >
            Sim, encerrar
          </Button>
        </DialogActions>
      </Dialog>

      <UIDialogConfirm
        open={alert}
        close={() => setAlert(false)}
        data={closeStockInfo}
        setrequestinfo={closeAlert}
        messageSucess={
          closeStockInfo.data
            ? closeStockInfo.data.message
            : 'Estoque encerrado com sucesso!'
        }
        type="success"
      />

      {/* Dialog: Seleção de Período */}
      <Dialog
        open={selectNewClosure}
        fullWidth
        onClose={handleModalSelectVigency}
        aria-labelledby="select-period-dialog-title"
      >
        <UIDialogTitle id="select-period-dialog-title">Escolha o período</UIDialogTitle>
        <DialogContent>
          <List>
            {cashierMoveInfo.data &&
              cashierMoveInfo.data.map((closureId, _index) => (
                <ListItem
                  key={`period-${closureId.id || _index}`}
                  button
                  selected={_index === index}
                  onClick={() => setNewIndex(_index)}
                  disabled={_index === index}
                  className={mainClasses.periodListItem}
                  style={{
                    borderLeft: _index === index ? '4px solid #17497F' : '4px solid transparent',
                  }}
                >
                  <ListItemText
                    primary={
                      !closureId.dueDate
                        ? `Em aberto desde ${formattedDateTime(closureId.startDate)}`
                        : `${formattedDateTime(closureId.startDate)} → ${formattedDateTime(closureId.dueDate)}`
                    }
                    secondary={!closureId.dueDate ? 'Vigência atual' : null}
                  />
                  {!closureId.dueDate && (
                    <Chip
                      label="Atual"
                      size="small"
                      style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                    />
                  )}
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalSelectVigency} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fab: Voltar de "Outros" */}
      {othersValues && (tab === '1' || tab === '2') && (
        <Tooltip title="Fechar 'Outros'">
          <Fab
            className={mainClasses.fabVoltar}
            size="small"
            variant="extended"
            onClick={toggleBackForOthers}
          >
            Voltar
          </Fab>
        </Tooltip>
      )}
    </>
  );
};

export default CashierClosuresView;
